import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2 } from "lucide-react";
import moment from "moment-timezone";
import Button from "components/shared/Button";
import { useCompanyDateFormat, useCompanyTimeFormat } from "utils/hooks/company";
import { renderDurationAsFormat } from "utils/helpers/date";
import { renderUserName } from "utils/helpers/user";

const columnHelper = createColumnHelper<any>();

interface ColumnProps {
    edit?: (row: any) => void;
    remove?: (id: string) => void;
}

export const useColumns = ({ edit, remove }: ColumnProps) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();

    return useMemo(() => {
        const columns: any[] = [
            columnHelper.accessor("startEffectiveDate", {
                header: t("start-effective-date"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "—";
                },
            }),
            columnHelper.accessor("endEffectiveDate", {
                header: t("end-effective-date"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "—";
                },
            }),
            columnHelper.accessor("expectedHours", {
                header: t("expected-hours"),
                cell: (info) => {
                    const value = info.getValue();
                    if (value == null) return "—";
                    return renderDurationAsFormat(Number(value), timeFormat ?? "HH:mm");
                },
            }),
            columnHelper.accessor("expectedWorkingDays", {
                header: t("expected-working-days"),
                cell: (info) => {
                    const value = info.getValue();
                    if (!Array.isArray(value) || value.length === 0) return "—";
                    return value.map((day) => t(typeof day === "string" ? day.toLowerCase() : day)).join(", ");
                },
            }),
            columnHelper.accessor("expectedWorkingDaysCount", {
                header: t("expected-working-days-count"),
                cell: (info) => {
                    const value = info.getValue();
                    return value != null ? String(value) : "—";
                },
            }),
            columnHelper.accessor("createdAt", {
                header: t("created-at"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "—";
                },
            }),
            columnHelper.accessor("createdBy", {
                header: t("created-by"),
                cell: (info) => renderUserName(info.getValue()) || "—",
            }),
            columnHelper.accessor("updatedAt", {
                header: t("updated-at"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "—";
                },
            }),
            columnHelper.accessor("updatedBy", {
                header: t("updated-by"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? renderUserName(value) : "—";
                },
            }),
        ];

        if (edit || remove) {
            columns.push(
                columnHelper.display({
                    id: "actions",
                    enableHiding: false,
                    enableColumnFilter: false,
                    header: () => <div className="tw-text-right">{t("actions")}</div>,
                    cell: ({ row: { original } }) => (
                        <div className="tw-flex tw-gap-1 tw-justify-end">
                            {edit && (
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => edit(original)}
                                    tooltip={t("edit")}
                                >
                                    <Pencil className="tw-h-4 tw-w-4" />
                                </Button>
                            )}
                            {remove && (
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => remove(original.id)}
                                    tooltip={t("delete")}
                                >
                                    <Trash2 className="tw-h-4 tw-w-4" />
                                </Button>
                            )}
                        </div>
                    ),
                })
            );
        }

        return columns;
    }, [edit, remove, t, dateFormat, timeFormat]);
};
